import { useCallback, useEffect, useState } from 'react'
import { sortBy, startCase } from 'lodash-es'
import AddIcon from '@mui/icons-material/Add'

import { ISelectOption } from '$/types'
import {
  findLookupTablesFromBaseSettings,
  useGetBaseSettingsQuery
} from '$/app/services/api/basesettings'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'

import AttributeField from './AttributeField'
import { EMPTY_RULE } from './emptyRyle'
import { scenarioToRuleWithId } from './scenarioToRuleWithId'
import {
  EditScenarioViewProps,
  IRuleWithId,
  IThreatWithCode,
  updateConditionsInRule
} from './types'
import { useValidatorHandlers } from './utils/useValidatorHandlers'
import { ViewModeShell } from './ViewModeShell'

export const EditScenarioView = ({
  scenario,
  scenarioName,
  scenarioRules,
  setScenarioName,
  setScenarioRules,
  scenarioModaltitle,
  scenarioNameValidationMessage,
  isScenarioNameValid,
  setIsScenarioNameEmpty,
  setSelectedThreat,
  selectedThreat,
  disableDropdownClearButtons = false
}: EditScenarioViewProps) => {
  const [ruleId, setRuleId] = useState<number>(
    scenario.rules.length === 0 ? 1 : scenario.rules.length
  )

  const { lookupTables } = useGetBaseSettingsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      lookupTables: findLookupTablesFromBaseSettings(data)
    })
  })

  const threatOptions = (lookupTables?.details ?? []).reduce(
    (acc: ISelectOption[], table) => {
      if (table.key === 'Threat') {
        ;(table.value as IThreatWithCode[]).forEach((threat) => {
          if (threat.TypeCode === 'POF') {
            acc.push({
              label: startCase(threat.Name),
              value: threat.Id.toString()
            })
          }
        })
      }

      return acc
    },
    []
  )

  useEffect(() => {
    setScenarioRules(scenarioToRuleWithId(scenario.rules))
  }, [scenario.rules, setScenarioRules])

  const addRule = (): void => {
    setScenarioRules((state) =>
      state.concat({
        ...EMPTY_RULE,
        id: ruleId
      })
    )
    setRuleId(ruleId + 1)
  }

  const removeRule = (id: number): void => {
    setScenarioRules(scenarioRules.filter((rule) => rule.id !== id))
  }

  const editRule = (id: number, newRule: IRuleWithId): void => {
    setScenarioRules((state) =>
      state.map((rule) => {
        if (rule.id === id) {
          return newRule
        }

        return rule
      })
    )
  }

  // ! TEMP Fix for updating condition without loop
  const updateConditionsInRule = useCallback<updateConditionsInRule>(
    (id, conditionsWithId) =>
      setScenarioRules((state) =>
        state.map((rule) => {
          if (rule.id === id) {
            return {
              ...rule,
              conditions: conditionsWithId.map(
                ({ id, ...condition }) => condition
              )
            }
          }

          return rule
        })
      ),
    [setScenarioRules]
  )

  const validatorHandlers = useValidatorHandlers(setIsScenarioNameEmpty)

  const handleScenarioNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const trimmedValue = event.target.value.trim()

    setScenarioName(trimmedValue)
  }

  return (
    <ViewModeShell>
      <div>
        <OverlayScrollbar scrollStyle='h-[500px] pr-3 pl-1'>
          <div className='mb-3'>
            <div className='my-2'>
              <label className='text-3xl font-semibold'>
                {scenarioModaltitle}
              </label>
            </div>
            <label htmlFor='scenario-name-input' className='pb-2 font-semibold'>
              Scenario Name*
            </label>
            <div>
              <input
                id='scenario-name-input'
                className={`mt-1 w-72 rounded-sm ${!isScenarioNameValid ? 'border-red-danger' : ''}`}
                type='text'
                placeholder='E.g. sample state'
                onChange={handleScenarioNameChange}
                value={scenarioName}
                onBlur={validatorHandlers.onFocusOut}
                onFocus={() => setIsScenarioNameEmpty(false)}
              />
              <p className='mt-2 text-sm font-medium text-red-danger'>
                {scenarioNameValidationMessage}
              </p>
            </div>
          </div>
          <div className='mb-3'>
            <label htmlFor='threat-input' className='pb-2 font-semibold'>
              Threat
            </label>
            <div>
              <SelectDropdownWrapper
                id='threat-input'
                className='my-1 w-72'
                disableClear={disableDropdownClearButtons}
                options={sortBy(threatOptions, 'label').map((option) => ({
                  ...option,
                  value: String(option.value)
                }))}
                value={String(selectedThreat.value || '')}
                onChange={(_, selection) => {
                  if (!Array.isArray(selection)) {
                    setSelectedThreat(selection as ISelectOption)
                  }
                }}
                placeholder='Select threat'
                onClear={
                  selectedThreat.label !== ''
                    ? () => setSelectedThreat({ label: '', value: '' })
                    : undefined
                }
              />
            </div>
          </div>
          <div className='mb-3'>
            <label htmlFor='attributes-input' className='pb-2 font-semibold'>
              Attributes*
            </label>
            <div>
              {scenarioRules.map((rule) => {
                return (
                  <AttributeField
                    key={rule.id}
                    disableDropdownClear={disableDropdownClearButtons}
                    selectedThreat={selectedThreat}
                    removeRule={() => removeRule(rule.id)}
                    ruleValues={rule}
                    editRule={(rule: IRuleWithId) => editRule(rule.id, rule)}
                    updateConditionsInRule={updateConditionsInRule}
                  />
                )
              })}
            </div>
            {scenarioRules[scenarioRules.length - 1]?.attributeName !== '' && (
              <button
                type='button'
                className='mt-2 align-middle font-medium text-lighthouseBlue'
                onClick={addRule}
              >
                <AddIcon className='mr-2' />
                Add Another Field
              </button>
            )}
          </div>
        </OverlayScrollbar>
      </div>
    </ViewModeShell>
  )
}

export default EditScenarioView
